import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const OrganizationContentTracks = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isSuperAdmin } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch organization
        const orgDoc = await getDoc(doc(db, 'organizations', orgId));
        if (orgDoc.exists()) {
          setOrganization(orgDoc.data());
        }

        // Fetch tracks for this organization
        const tracksRef = collection(db, 'contentTracks');
        const tracksQuery = query(
          tracksRef,
          where('organizationId', '==', orgId)
        );
        const tracksSnapshot = await getDocs(tracksQuery);
        
        const tracksData = tracksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by creation date (newest first)
        tracksData.sort((a, b) => {
          const aDate = a.dateCreated?.toDate?.() || new Date(0);
          const bDate = b.dateCreated?.toDate?.() || new Date(0);
          return bDate - aDate;
        });

        setTracks(tracksData);
      } catch (error) {
        console.error('Error fetching content tracks:', error);
      } finally {
        setLoading(false);
      }
    };

    if (orgId) {
      fetchData();
    }
  }, [orgId]);

  const handleDelete = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this content track? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'contentTracks', trackId));
      setTracks(prev => prev.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Error deleting track:', error);
      alert('Failed to delete track');
    }
  };

  const getApprovalStatusBadge = (track) => {
    if (isSuperAdmin()) {
      // Super admins don't need approval
      return null;
    }

    const approvalStatus = track.approvalStatus || 'pending';
    
    if (approvalStatus === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-4 w-4 mr-1" />
          Approved
        </span>
      );
    } else if (approvalStatus === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="h-4 w-4 mr-1" />
          Rejected
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending Approval
        </span>
      );
    }
  };

  const getVisibilityBadge = (track) => {
    const visibility = track.visibility || {};
    if (visibility.isPublic) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Public
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Organization Only
        </span>
      );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto p-4">
          <div className="text-center py-12">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <Link 
            to={`/organization/${orgId}`}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
          >
            ← Back to Organization
          </Link>
          <div className="flex justify-between items-center mt-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Content Tracks
              </h1>
              <p className="text-gray-600 mt-1">
                {organization?.name || 'Organization'} - Manage content tracks for your organization
              </p>
            </div>
            <Link
              to={`/organizations/${orgId}/content-tracks/create`}
              className="inline-flex items-center px-4 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939] transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Track
            </Link>
          </div>
        </div>

        {tracks.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No content tracks yet</p>
            <p className="text-gray-400 mb-6">Create your first content track to get started</p>
            <Link
              to={`/organizations/${orgId}/content-tracks/create`}
              className="inline-flex items-center px-4 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939] transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Track
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tracks.map(track => (
              <div key={track.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {track.name}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {track.description || 'No description'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {getApprovalStatusBadge(track)}
                  {getVisibilityBadge(track)}
                  {track.status && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      track.status === 'published' 
                        ? 'bg-green-100 text-green-800'
                        : track.status === 'draft'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {track.status}
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    <Link
                      to={`/content-tracks/${track.id}`}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                      title="View"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/content-tracks/${track.id}/edit`}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(track.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrganizationContentTracks;
